import { isReadOnly } from '@/lib/permissions'
import { type PatientFile, useDeletePatientFileMutation, usePatientFilesQuery } from '@/services/patient-files'
import { borderRadius, colors } from '@/theme'
import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material'

interface DeletePatientFileDialogProps {
  open: boolean
  patientHash: string
  file: PatientFile | null
  onClose: () => void
}

export default function DeletePatientFileDialog({ open, patientHash, file, onClose }: DeletePatientFileDialogProps) {
  const deleteMutation = useDeletePatientFileMutation(patientHash)
  const { refetch: refetchFiles } = usePatientFilesQuery(patientHash)
  const isLoading = deleteMutation.isPending

  const handleDelete = async () => {
    if (isReadOnly()) return
    if (!file) return

    deleteMutation.mutateAsync(file.id, {
      onSuccess: async () => {
        refetchFiles()
        onClose()
      }
    })
  }

  return (
    <Dialog
      open={open}
      onClose={isLoading ? undefined : onClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{ sx: { borderRadius: borderRadius.md } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          pb: 1
        }}
      >
        <Typography variant='sectionTitle'>Delete file</Typography>
        <IconButton onClick={onClose} size='small' sx={{ mt: -0.5 }} disabled={isLoading}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography sx={{ fontSize: 14, color: colors.text.primary, mb: 1.5 }}>
          Delete <strong>{file?.originalFilename ?? 'this file'}</strong>?
        </Typography>
        <Typography sx={{ fontSize: 14, color: colors.text.secondary }}>
          The file disappears from the prescriber's view and every file list immediately. The stored copy is retained
          for a retention period so existing consultation references keep working, then permanently purged.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button variant='ghost' onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant='primary'
          color='error'
          startIcon={<DeleteOutlineIcon />}
          onClick={handleDelete}
          disabled={isLoading}
          loading={isLoading}
        >
          Delete file
        </Button>
      </DialogActions>
    </Dialog>
  )
}
